/**
 * Scan -> delivery conversion funnel (epic-6).
 *
 * Renders the canonical funnel stages as a horizontal, aurora-gradient bar
 * funnel: each stage's bar is sized relative to the funnel's top (widest) value,
 * with the absolute count and the step-over-step retention % shown inline.
 *
 * Accessibility: the funnel is built from a real <table> with a caption,
 * scope'd headers and a visually-hidden header row — so screen readers get a
 * clear tabular reading of every stage + count, satisfying the "data table
 * alternative" guideline. The bars are decorative (aria-hidden); the numbers are
 * the source of truth and are always visible as text.
 */
import { FUNNEL_STAGES, formatCount } from './funnelLabels';

interface Props {
  /** Canonical {event_type: count}; missing keys treated as 0. */
  funnel: Record<string, number>;
}

export function FunnelChart({ funnel }: Props) {
  const stages = FUNNEL_STAGES.map((s) => ({
    ...s,
    count: Math.max(0, Number(funnel[s.key] ?? 0)),
  }));

  // Bars are sized relative to the largest stage (typically the top of funnel).
  const max = stages.reduce((m, s) => Math.max(m, s.count), 0);
  const hasAnyData = max > 0;

  return (
    <table className="w-full border-collapse text-left">
      <caption className="mb-4 text-left text-sm text-ink-500">
        Engagement funnel — from QR scan through to prize delivery.
      </caption>
      <thead className="sr-only">
        <tr>
          <th scope="col">Stage</th>
          <th scope="col">Count</th>
          <th scope="col">Retention from previous stage</th>
        </tr>
      </thead>
      <tbody>
        {stages.map((stage, i) => {
          const widthPct = max > 0 ? Math.max((stage.count / max) * 100, stage.count > 0 ? 4 : 0) : 0;
          // Step-over-step retention vs the previous stage (skip the first).
          const prev = i > 0 ? stages[i - 1].count : null;
          const retention =
            prev && prev > 0 ? `${((stage.count / prev) * 100).toFixed(0)}%` : null;

          return (
            <tr key={stage.key} className="align-middle">
              <th
                scope="row"
                className="w-40 py-2 pr-4 text-sm font-medium text-ink-700"
              >
                {stage.label}
              </th>
              <td className="py-2">
                <div className="flex items-center gap-3">
                  {/* No bar at all for a zero-count stage, so an empty stage is
                      never misrepresented as a sliver of activity. */}
                  {stage.count > 0 ? (
                    <div
                      aria-hidden="true"
                      className="h-7 min-w-[2px] rounded-md bg-aurora transition-[width] duration-300 motion-reduce:transition-none"
                      style={{ width: `${widthPct}%` }}
                    />
                  ) : null}
                  <span className="tabular-nums text-sm font-semibold text-ink">
                    {formatCount(stage.count)}
                  </span>
                </div>
              </td>
              <td className="w-28 py-2 pl-3 text-right text-xs tabular-nums text-ink-500">
                {retention ? <span title="Retention from previous stage">{retention}</span> : null}
              </td>
            </tr>
          );
        })}
      </tbody>
      {!hasAnyData ? (
        <tfoot>
          <tr>
            <td colSpan={3} className="pt-4 text-sm text-ink-500">
              Awaiting data — funnel activity will appear here once your campaign
              goes live.
            </td>
          </tr>
        </tfoot>
      ) : null}
    </table>
  );
}

export default FunnelChart;
